/**
 * Initial sitemap state for the canvas — the default new-site sitemap and the
 * helper that seeds the controlled nodes/edges from an (already-replayed) Y.Doc.
 *
 * Why read from the doc: the canvas's controlled `useNodesState` is xyflow's
 * source of truth. If it started at DEFAULT_NODES while the persisted doc held
 * real pages, the first onNodesChange (e.g. node-dimensions on mount) would push
 * the stale defaults through bindYjsToFlow → diffIntoMap, DELETING the persisted
 * pages and resetting labels to "Home". Seeding the controlled state from the doc
 * keeps controlled-state / store / doc consistent from the first frame.
 */
import type * as Y from "yjs";
import type { Node, Edge } from "./xyflow.js";
import { PAGE_WIDTH, type PageNodeData } from "./usePageNode.js";

// Bootstrap dimensions stamped on every controlled node before it reaches
// xyflow. xyflow renders a node `visibility:hidden` until `nodeHasDimensions`
// is true — which needs `measured` OR `width/height` OR `initialWidth/
// initialHeight` to be DEFINED. Persisted nodes carry none of these, so on a
// PRODUCTION first paint (React dev-mode's double render hid this) the node —
// and every widget in it — stayed hidden and NON-INTERACTIVE: clicks fell
// through to the pan surface, so you couldn't select or edit anything until an
// unrelated re-render finally flushed a measurement (long-standing "can't edit
// text" report, 2026-07-20). These are SEED-ONLY: the node's ResizeObserver
// overwrites `measured` with the true content size on the first frame, so the
// dynamic page height is unaffected — they exist purely so the node is shown +
// hittable from frame 1. Height is a rough guess; only being defined matters.
export const SEED_WIDTH = PAGE_WIDTH;
export const SEED_HEIGHT = 600;

function withSeedDimensions(node: Node<PageNodeData>): Node<PageNodeData> {
  if (
    node.width != null ||
    node.height != null ||
    node.initialWidth != null ||
    node.initialHeight != null ||
    node.measured?.width != null
  ) {
    return node;
  }
  return { ...node, initialWidth: SEED_WIDTH, initialHeight: SEED_HEIGHT };
}

export const DEFAULT_NODES: Node<PageNodeData>[] = [
  {
    id: "home",
    type: "page",
    position: { x: 80, y: 80 },
    initialWidth: SEED_WIDTH,
    initialHeight: SEED_HEIGHT,
    data: { label: "Home" },
  },
];

export const DEFAULT_EDGES: Edge[] = [];

/**
 * Read the persisted sitemap out of the doc, falling back to the new-site
 * defaults when the doc is empty. Call once at canvas mount (after
 * useEditorDoc's `loaded` gate, so the doc already holds the replayed state).
 *
 * `seedDefault` (EditorDoc.canSeed) must be true to fall back to DEFAULT_NODES:
 * only the authoritative tab may create a new site. A FOLLOWER whose sync
 * handshake hasn't completed (an empty doc under the anti-hang fallback) passes
 * false and mounts EMPTY — seeding there would push a duplicate default sitemap
 * into the shared doc. Once real state arrives over BroadcastChannel the binding
 * populates the canvas.
 */
export function readInitialSitemap(
  ydoc: Y.Doc,
  seedDefault = true,
): {
  nodes: Node<PageNodeData>[];
  edges: Edge[];
} {
  const yNodes = [...ydoc.getMap("nodes").values()] as Node<PageNodeData>[];
  const yEdges = [...ydoc.getMap("edges").values()] as Edge[];
  if (yNodes.length > 0)
    return { nodes: yNodes.map(withSeedDimensions), edges: yEdges };
  // Empty doc: seed defaults only if authoritative; otherwise stay empty.
  return seedDefault
    ? { nodes: DEFAULT_NODES, edges: DEFAULT_EDGES }
    : { nodes: [], edges: [] };
}
